import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tag, ArrowClockwise, FloppyDisk, Printer, Plus, X } from "@phosphor-icons/react";
import { renderPosTicket80Html, SAMPLE_PEDIDO } from "@/lib/labels/posTicket80";
import { renderPosTicketFiscal80Html, SAMPLE_FACTURA } from "@/lib/labels/posTicketFiscal80";
import { renderAlivioCaja80Html, SAMPLE_ALIVIO } from "@/lib/labels/posAlivioCaja80";
import NumberInput from "@/components/ui/NumberInput";

/**
 * Admin → Edición de Etiquetas.
 *
 * Muestra un listado (sidebar interno) con todas las plantillas configurables
 * (POS ticket 80mm, Zebra trazabilidad, Góndola, …). Al seleccionar una,
 * se abre un editor con textos, secciones y fuentes + preview en vivo.
 */
export default function AdminLabelsPage() {
  const [templates, setTemplates] = useState([]);
  const [selectedSlug, setSelectedSlug] = useState("pos_ticket_80mm");
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const loadTemplates = useCallback(async () => {
    try {
      const { data } = await api.get("/label-templates");
      setTemplates(data || []);
      const sel = (data || []).find((t) => t.slug === selectedSlug) || data?.[0];
      if (sel) {
        setSelectedSlug(sel.slug);
        setConfig(structuredClone(sel.config));
        setDefaults(structuredClone(sel.defaults));
        setDirty(false);
      }
    } catch (e) {
      toast.error("No se pudieron cargar las plantillas");
    }
  }, [selectedSlug]);

  useEffect(() => { loadTemplates(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectTemplate = async (slug) => {
    if (dirty && !window.confirm("Tenés cambios sin guardar. ¿Descartarlos?")) return;
    setSelectedSlug(slug);
    try {
      const { data } = await api.get(`/label-templates/${slug}`);
      setConfig(structuredClone(data.config));
      setDefaults(structuredClone(data.defaults));
      setDirty(false);
    } catch (e) {
      toast.error("Error cargando la plantilla");
    }
  };

  const patchConfig = (fn) => {
    setConfig((prev) => {
      const next = structuredClone(prev);
      fn(next);
      return next;
    });
    setDirty(true);
  };

  const guardar = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await api.put(`/label-templates/${selectedSlug}`, { config });
      toast.success("Plantilla guardada");
      setDirty(false);
      loadTemplates();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error guardando");
    } finally {
      setSaving(false);
    }
  };

  const restaurar = async () => {
    if (!window.confirm("¿Restaurar la plantilla a los valores originales? Se perderán los cambios guardados.")) return;
    try {
      await api.post(`/label-templates/${selectedSlug}/reset`);
      toast.success("Restaurada a los defaults");
      loadTemplates();
    } catch (e) {
      toast.error("No se pudo restaurar");
    }
  };

  const imprimirPrueba = async () => {
    if (!config) return;
    let html;
    if (selectedSlug === "pos_ticket_80mm") {
      html = renderPosTicket80Html(SAMPLE_PEDIDO, config, { autoprint: true });
    } else if (selectedSlug === "pos_ticket_fiscal_80mm") {
      // Cargar el logo desde el backend para incluirlo en el ticket de prueba
      let logoDataUrl = "";
      try {
        const resp = await api.get("/empresa/logo", { responseType: "blob" });
        logoDataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => resolve("");
          reader.readAsDataURL(resp.data);
        });
      } catch (_) { /* logo opcional */ }
      html = renderPosTicketFiscal80Html(SAMPLE_FACTURA, config, {
        autoprint: true,
        logo: logoDataUrl,
        empresa: { razon_social: "LA BIFERIA S.R.L.", cuit: "30-70123456-7", condicion_iva: "IVA Responsable Inscripto", ingresos_brutos: "902-1234567-8", inicio_actividades: "01/01/2020", domicilio: "Av. Corrientes 1234, CABA" },
      });
    } else if (selectedSlug === "pos_alivio_caja_80mm") {
      html = renderAlivioCaja80Html(SAMPLE_ALIVIO, config, { autoprint: true });
    } else {
      return;
    }
    const w = window.open("", "_blank", "width=380,height=650");
    w.document.write(html);
    w.document.close();
  };

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.slug === selectedSlug),
    [templates, selectedSlug]
  );

  return (
    <>
      <Topbar
        title="Edición de Etiquetas"
        subtitle="Configuración de plantillas de tickets y etiquetas del sistema"
      />
      <div className="p-4 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
        {/* Sidebar interno con todas las plantillas */}
        <aside className="border border-[#E5E5E5] bg-white rounded-sm" data-testid="labels-sidebar">
          <div className="px-4 py-3 border-b border-[#E5E5E5]">
            <h2 className="font-heading font-semibold text-[14px]">Plantillas</h2>
          </div>
          <ul>
            {templates.map((t) => {
              const active = t.slug === selectedSlug;
              const disabled = t.estado === "proximamente";
              return (
                <li key={t.slug}>
                  <button
                    type="button"
                    data-testid={`label-slug-${t.slug}`}
                    disabled={disabled}
                    onClick={() => selectTemplate(t.slug)}
                    className={`w-full text-left px-4 py-3 border-l-2 transition
                      ${active
                        ? "bg-[#6B1F24]/5 border-[#6B1F24]"
                        : "border-transparent hover:bg-neutral-50"}
                      ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      <Tag size={14} weight="bold" className={active ? "text-[#6B1F24]" : "text-[#666]"} />
                      <span className="text-[13px] font-medium">{t.nombre}</span>
                    </div>
                    <div className="text-[11px] text-[#666] font-mono mt-1 line-clamp-2">{t.descripcion}</div>
                    {disabled && (
                      <span className="inline-block mt-1 text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded-sm">
                        próximamente
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Editor + preview */}
        <div className="space-y-4">
          {config && selectedTemplate && (
            <>
              <div className="border border-[#E5E5E5] bg-white rounded-sm px-4 py-3 flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="font-heading font-semibold text-[15px]">{selectedTemplate.nombre}</div>
                  <div className="text-[11px] text-[#666] font-mono">
                    {selectedTemplate.updated_at
                      ? `Última edición: ${new Date(selectedTemplate.updated_at).toLocaleString("es-AR")} · ${selectedTemplate.updated_by || "—"}`
                      : "Sin ediciones · usando defaults"}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {(selectedSlug === "pos_ticket_80mm" || selectedSlug === "pos_ticket_fiscal_80mm" || selectedSlug === "pos_alivio_caja_80mm") && (
                    <Button
                      variant="outline"
                      onClick={imprimirPrueba}
                      className="rounded-sm h-9"
                      data-testid="btn-imprimir-prueba"
                    >
                      <Printer size={14} className="mr-1" /> Ticket de prueba
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={restaurar}
                    className="rounded-sm h-9"
                    data-testid="btn-restaurar"
                  >
                    <ArrowClockwise size={14} className="mr-1" /> Restaurar defaults
                  </Button>
                  <Button
                    onClick={guardar}
                    disabled={saving || !dirty}
                    className="rounded-sm h-9 bg-[#6B1F24] hover:bg-[#571519]"
                    data-testid="btn-guardar"
                  >
                    <FloppyDisk size={14} className="mr-1" /> {saving ? "Guardando…" : "Guardar cambios"}
                  </Button>
                </div>
              </div>

              {selectedSlug === "pos_ticket_80mm" ? (
                <>
                  <AutoprintConfigPanel />
                  <BarcodeBalanzaPanel />
                  <PosTicketEditor config={config} defaults={defaults} patchConfig={patchConfig} />
                </>
              ) : selectedSlug === "pos_ticket_fiscal_80mm" ? (
                <PosTicketFiscalEditor config={config} defaults={defaults} patchConfig={patchConfig} />
              ) : selectedSlug === "pos_alivio_caja_80mm" ? (
                <AlivioCajaEditor config={config} defaults={defaults} patchConfig={patchConfig} />
              ) : (selectedSlug === "zebra_traceability" || selectedSlug === "zebra_output") ? (
                <ZebraTraceabilityEditor config={config} defaults={defaults} patchConfig={patchConfig} />
              ) : (
                <div className="border border-[#E5E5E5] bg-white rounded-sm p-6 text-center text-[#666]">
                  Plantilla en desarrollo. Vuelve pronto.
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* -----------------------------------------------------------------------
 * Editor específico del ticket POS 80mm
 * ---------------------------------------------------------------------*/
function PosTicketEditor({ config, defaults, patchConfig }) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
      <div className="border border-[#E5E5E5] bg-white rounded-sm">
        <Tabs defaultValue="textos">
          <TabsList className="w-full justify-start rounded-none border-b border-[#E5E5E5] bg-neutral-50 h-11 px-2">
            <TabsTrigger value="textos" data-testid="tab-textos">Textos</TabsTrigger>
            <TabsTrigger value="secciones" data-testid="tab-secciones">Secciones</TabsTrigger>
            <TabsTrigger value="fuentes" data-testid="tab-fuentes">Fuentes</TabsTrigger>
            <TabsTrigger value="layout" data-testid="tab-layout">Layout</TabsTrigger>
          </TabsList>

          <TabsContent value="textos" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(defaults.textos || {}).map(([k, dv]) => (
                <div key={k}>
                  <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                    {labelForTextKey(k)}
                  </Label>
                  <Input
                    className="rounded-sm mt-1 font-mono text-[13px]"
                    data-testid={`text-${k}`}
                    value={config.textos?.[k] ?? ""}
                    placeholder={dv}
                    onChange={(e) => patchConfig((c) => { c.textos ||= {}; c.textos[k] = e.target.value; })}
                  />
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="secciones" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(defaults.secciones || {}).map(([k]) => (
                <label
                  key={k}
                  className="flex items-center gap-2 py-2 px-3 border border-[#E5E5E5] rounded-sm cursor-pointer hover:bg-neutral-50"
                >
                  <input
                    type="checkbox"
                    data-testid={`seccion-${k}`}
                    checked={!!config.secciones?.[k]}
                    onChange={(e) => patchConfig((c) => { c.secciones ||= {}; c.secciones[k] = e.target.checked; })}
                  />
                  <span className="text-[13px]">{labelForSectionKey(k)}</span>
                </label>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="fuentes" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(defaults.fuentes || {}).map(([k, dv]) => (
                <div key={k}>
                  <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                    {labelForFontKey(k)} <span className="text-[#999]">(default {dv}px)</span>
                  </Label>
                  <NumberInput
                    min={8}
                    max={40}
                    className="rounded-sm mt-1 font-mono text-[13px] w-28"
                    data-testid={`font-${k}`}
                    value={config.fuentes?.[k] ?? dv}
                    onChange={(e) => patchConfig((c) => { c.fuentes ||= {}; c.fuentes[k] = parseInt(e.target.value || dv, 10); })}
                  />
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="layout" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  Ancho útil (mm) <span className="text-[#999]">(default {defaults.layout?.ancho_mm}mm)</span>
                </Label>
                <NumberInput
                  min={40}
                  max={110}
                  className="rounded-sm mt-1 font-mono text-[13px] w-28"
                  data-testid="layout-ancho"
                  value={config.layout?.ancho_mm ?? defaults.layout?.ancho_mm}
                  onChange={(e) => patchConfig((c) => { c.layout ||= {}; c.layout.ancho_mm = parseInt(e.target.value || 72, 10); })}
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  Margen (mm) <span className="text-[#999]">(default {defaults.layout?.margen_mm}mm)</span>
                </Label>
                <NumberInput
                  min={0}
                  max={10}
                  step={0.5}
                  className="rounded-sm mt-1 font-mono text-[13px] w-28"
                  data-testid="layout-margen"
                  value={config.layout?.margen_mm ?? defaults.layout?.margen_mm}
                  onChange={(e) => patchConfig((c) => { c.layout ||= {}; c.layout.margen_mm = parseFloat(e.target.value || 2); })}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Preview */}
      <TicketPreview config={config} />
    </div>
  );
}

function TicketPreview({ config }) {
  const html = useMemo(
    () => renderPosTicket80Html(SAMPLE_PEDIDO, config, { autoprint: false, preview: true }),
    [config]
  );
  return (
    <div className="border border-[#E5E5E5] bg-neutral-50 rounded-sm sticky top-4 self-start">
      <div className="px-4 py-2 border-b border-[#E5E5E5] bg-white flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#666]">Vista previa (datos de ejemplo)</div>
      </div>
      <div className="p-4 flex justify-center">
        <iframe
          data-testid="ticket-preview"
          title="Preview ticket 80mm"
          srcDoc={html}
          style={{ width: `${(config.layout?.ancho_mm ?? 72) + 8}mm`, height: 620, border: "1px solid #ddd", background: "#fff" }}
        />
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Editor específico del Ticket de Alivio de Caja 80mm
 * ---------------------------------------------------------------------*/
function AlivioCajaEditor({ config, defaults, patchConfig }) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
      <div className="border border-[#E5E5E5] bg-white rounded-sm">
        <Tabs defaultValue="textos">
          <TabsList className="w-full justify-start rounded-none border-b border-[#E5E5E5] bg-neutral-50 h-11 px-2">
            <TabsTrigger value="textos" data-testid="tab-textos">Textos</TabsTrigger>
            <TabsTrigger value="secciones" data-testid="tab-secciones">Secciones</TabsTrigger>
            <TabsTrigger value="fuentes" data-testid="tab-fuentes">Fuentes</TabsTrigger>
            <TabsTrigger value="layout" data-testid="tab-layout">Layout</TabsTrigger>
          </TabsList>

          <TabsContent value="textos" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(defaults.textos || {}).map(([k, dv]) => (
                <div key={k}>
                  <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                    {labelForAlivioTextKey(k)}
                  </Label>
                  <Input
                    className="rounded-sm mt-1 font-mono text-[13px]"
                    data-testid={`text-${k}`}
                    value={config.textos?.[k] ?? ""}
                    placeholder={dv}
                    onChange={(e) => patchConfig((c) => { c.textos ||= {}; c.textos[k] = e.target.value; })}
                  />
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="secciones" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(defaults.secciones || {}).map(([k]) => (
                <label
                  key={k}
                  className="flex items-center gap-2 py-2 px-3 border border-[#E5E5E5] rounded-sm cursor-pointer hover:bg-neutral-50"
                >
                  <input
                    type="checkbox"
                    data-testid={`seccion-${k}`}
                    checked={!!config.secciones?.[k]}
                    onChange={(e) => patchConfig((c) => { c.secciones ||= {}; c.secciones[k] = e.target.checked; })}
                  />
                  <span className="text-[13px]">{labelForAlivioSectionKey(k)}</span>
                </label>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="fuentes" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(defaults.fuentes || {}).map(([k, dv]) => (
                <div key={k}>
                  <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                    {labelForAlivioFontKey(k)} <span className="text-[#999]">(default {dv}px)</span>
                  </Label>
                  <NumberInput
                    min={8}
                    max={40}
                    className="rounded-sm mt-1 font-mono text-[13px] w-28"
                    data-testid={`font-${k}`}
                    value={config.fuentes?.[k] ?? dv}
                    onChange={(e) => patchConfig((c) => { c.fuentes ||= {}; c.fuentes[k] = parseInt(e.target.value || dv, 10); })}
                  />
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="layout" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  Ancho útil (mm) <span className="text-[#999]">(default {defaults.layout?.ancho_mm}mm)</span>
                </Label>
                <NumberInput
                  min={40}
                  max={110}
                  className="rounded-sm mt-1 font-mono text-[13px] w-28"
                  data-testid="layout-ancho"
                  value={config.layout?.ancho_mm ?? defaults.layout?.ancho_mm}
                  onChange={(e) => patchConfig((c) => { c.layout ||= {}; c.layout.ancho_mm = parseInt(e.target.value || 72, 10); })}
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  Margen (mm) <span className="text-[#999]">(default {defaults.layout?.margen_mm}mm)</span>
                </Label>
                <NumberInput
                  min={0}
                  max={10}
                  step={0.5}
                  className="rounded-sm mt-1 font-mono text-[13px] w-28"
                  data-testid="layout-margen"
                  value={config.layout?.margen_mm ?? defaults.layout?.margen_mm}
                  onChange={(e) => patchConfig((c) => { c.layout ||= {}; c.layout.margen_mm = parseFloat(e.target.value || 2); })}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <AlivioPreview config={config} />
    </div>
  );
}

function AlivioPreview({ config }) {
  const html = useMemo(
    () => renderAlivioCaja80Html(SAMPLE_ALIVIO, config, { autoprint: false, preview: true }),
    [config]
  );
  return (
    <div className="border border-[#E5E5E5] bg-neutral-50 rounded-sm sticky top-4 self-start">
      <div className="px-4 py-2 border-b border-[#E5E5E5] bg-white flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#666]">Vista previa (datos de ejemplo)</div>
      </div>
      <div className="p-4 flex justify-center">
        <iframe
          data-testid="alivio-preview"
          title="Preview alivio 80mm"
          srcDoc={html}
          style={{ width: `${(config.layout?.ancho_mm ?? 72) + 8}mm`, height: 560, border: "1px solid #ddd", background: "#fff" }}
        />
      </div>
    </div>
  );
}

function labelForAlivioTextKey(k) {
  const map = {
    titulo: "Título del comprobante",
    label_fecha: "Etiqueta 'Fecha:'",
    label_sucursal: "Etiqueta 'Sucursal:'",
    label_caja: "Etiqueta 'Caja:'",
    label_usuario: "Etiqueta 'Usuario:'",
    label_comentario: "Etiqueta 'Comentario:'",
    label_movimiento: "Etiqueta 'Movimiento:'",
    label_monto: "Etiqueta 'Monto:'",
    valor_movimiento: "Texto del movimiento (Egreso/Retiro)",
    sucursal: "Sucursal fija (opcional)",
    firma_usuario: "Leyenda firma usuario",
    firma_supervisor: "Leyenda firma supervisor",
  };
  return map[k] || k;
}

function labelForAlivioSectionKey(k) {
  const map = {
    titulo: "Título",
    fecha: "Fila Fecha",
    sucursal: "Fila Sucursal",
    caja: "Fila Caja",
    usuario: "Fila Usuario",
    comentario: "Fila Comentario",
    movimiento: "Fila Movimiento",
    monto: "Fila Monto",
    firma_usuario: "Línea Firma Usuario",
    firma_supervisor: "Línea Firma Supervisor",
  };
  return map[k] || k;
}

function labelForAlivioFontKey(k) {
  const map = {
    base_px: "Base (datos)",
    titulo_px: "Título",
    monto_px: "Monto",
  };
  return map[k] || k;
}

/* -----------------------------------------------------------------------
 * Editor específico del Ticket FISCAL POS 80mm (Factura A/B/C con CAE)
 * ---------------------------------------------------------------------*/
function PosTicketFiscalEditor({ config, defaults, patchConfig }) {
  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
      <div className="border border-[#E5E5E5] bg-white rounded-sm">
        <Tabs defaultValue="textos">
          <TabsList className="w-full justify-start rounded-none border-b border-[#E5E5E5] bg-neutral-50 h-11 px-2">
            <TabsTrigger value="textos" data-testid="fiscal-tab-textos">Textos</TabsTrigger>
            <TabsTrigger value="secciones" data-testid="fiscal-tab-secciones">Secciones</TabsTrigger>
            <TabsTrigger value="fuentes" data-testid="fiscal-tab-fuentes">Fuentes</TabsTrigger>
            <TabsTrigger value="layout" data-testid="fiscal-tab-layout">Layout</TabsTrigger>
          </TabsList>

          <TabsContent value="textos" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(defaults.textos || {}).map(([k, dv]) => (
                <div key={k}>
                  <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                    {labelForFiscalTextKey(k)}
                  </Label>
                  <Input
                    className="rounded-sm mt-1 font-mono text-[13px]"
                    data-testid={`fiscal-text-${k}`}
                    value={config.textos?.[k] ?? ""}
                    placeholder={dv}
                    onChange={(e) => patchConfig((c) => { c.textos ||= {}; c.textos[k] = e.target.value; })}
                  />
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="secciones" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(defaults.secciones || {}).map(([k]) => (
                <label
                  key={k}
                  className="flex items-center gap-2 py-2 px-3 border border-[#E5E5E5] rounded-sm cursor-pointer hover:bg-neutral-50"
                >
                  <input
                    type="checkbox"
                    data-testid={`fiscal-seccion-${k}`}
                    checked={!!config.secciones?.[k]}
                    onChange={(e) => patchConfig((c) => { c.secciones ||= {}; c.secciones[k] = e.target.checked; })}
                  />
                  <span className="text-[13px]">{labelForFiscalSectionKey(k)}</span>
                </label>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="fuentes" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(defaults.fuentes || {}).map(([k, dv]) => (
                <div key={k}>
                  <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                    {labelForFiscalFontKey(k)} <span className="text-[#999]">(default {dv}px)</span>
                  </Label>
                  <NumberInput
                    min={8}
                    max={60}
                    className="rounded-sm mt-1 font-mono text-[13px] w-28"
                    data-testid={`fiscal-font-${k}`}
                    value={config.fuentes?.[k] ?? dv}
                    onChange={(e) => patchConfig((c) => { c.fuentes ||= {}; c.fuentes[k] = parseInt(e.target.value || dv, 10); })}
                  />
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="layout" className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  Ancho útil (mm) <span className="text-[#999]">(default {defaults.layout?.ancho_mm}mm)</span>
                </Label>
                <NumberInput
                  min={40}
                  max={110}
                  className="rounded-sm mt-1 font-mono text-[13px] w-28"
                  data-testid="fiscal-layout-ancho"
                  value={config.layout?.ancho_mm ?? defaults.layout?.ancho_mm}
                  onChange={(e) => patchConfig((c) => { c.layout ||= {}; c.layout.ancho_mm = parseInt(e.target.value || 72, 10); })}
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  Margen (mm) <span className="text-[#999]">(default {defaults.layout?.margen_mm}mm)</span>
                </Label>
                <NumberInput
                  min={0}
                  max={10}
                  step={0.5}
                  className="rounded-sm mt-1 font-mono text-[13px] w-28"
                  data-testid="fiscal-layout-margen"
                  value={config.layout?.margen_mm ?? defaults.layout?.margen_mm}
                  onChange={(e) => patchConfig((c) => { c.layout ||= {}; c.layout.margen_mm = parseFloat(e.target.value || 2); })}
                />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-widest font-mono text-[#666]">
                  Tamaño QR AFIP (mm) <span className="text-[#999]">(default {defaults.layout?.qr_mm}mm)</span>
                </Label>
                <NumberInput
                  min={12}
                  max={40}
                  className="rounded-sm mt-1 font-mono text-[13px] w-28"
                  data-testid="fiscal-layout-qr"
                  value={config.layout?.qr_mm ?? defaults.layout?.qr_mm}
                  onChange={(e) => patchConfig((c) => { c.layout ||= {}; c.layout.qr_mm = parseInt(e.target.value || 22, 10); })}
                />
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Preview */}
      <TicketFiscalPreview config={config} />
    </div>
  );
}

function TicketFiscalPreview({ config }) {
  const [logo, setLogo] = useState("");
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const resp = await api.get("/empresa/logo", { responseType: "blob" });
        const dataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(resp.data);
        });
        if (alive) setLogo(dataUrl);
      } catch (_) {
        if (alive) setLogo("");
      }
    })();
    return () => { alive = false; };
  }, []);

  const html = useMemo(
    () => renderPosTicketFiscal80Html(SAMPLE_FACTURA, config, {
      autoprint: false,
      preview: true,
      logo,
      empresa: {
        razon_social: "LA BIFERIA S.R.L.",
        cuit: "30-70123456-7",
        condicion_iva: "IVA Responsable Inscripto",
        ingresos_brutos: "902-1234567-8",
        inicio_actividades: "01/01/2020",
        domicilio: "Av. Corrientes 1234, CABA",
      },
    }),
    [config, logo]
  );
  return (
    <div className="border border-[#E5E5E5] bg-neutral-50 rounded-sm sticky top-4 self-start">
      <div className="px-4 py-2 border-b border-[#E5E5E5] bg-white flex items-center justify-between">
        <div className="text-[11px] font-mono uppercase tracking-widest text-[#666]">Vista previa fiscal (datos de ejemplo)</div>
      </div>
      <div className="p-4 flex justify-center">
        <iframe
          data-testid="ticket-fiscal-preview"
          title="Preview ticket fiscal 80mm"
          srcDoc={html}
          style={{ width: `${(config.layout?.ancho_mm ?? 72) + 8}mm`, height: 720, border: "1px solid #ddd", background: "#fff" }}
        />
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Etiquetas amigables para los inputs
 * ---------------------------------------------------------------------*/
function labelForTextKey(k) {
  const map = {
    titulo_pedido: "Título encabezado",
    subtitulo: "Subtítulo",
    label_cliente: "Etiqueta 'Cliente:'",
    label_cuit: "Etiqueta CUIT/DNI",
    label_tel: "Etiqueta Teléfono",
    label_pago: "Etiqueta 'Pago:'",
    label_obs: "Etiqueta Observaciones",
    label_total: "Etiqueta TOTAL",
    label_subtotal: "Etiqueta Subtotal",
    label_envio: "Etiqueta Envío",
    label_descuento: "Etiqueta Descuento",
    footer: "Texto de despedida (pie)",
    col_cantidad: "Columna Cant.",
    col_detalle: "Columna Detalle",
  };
  return map[k] || k;
}

function labelForSectionKey(k) {
  const map = {
    logo: "Logo empresa",
    fecha_cabecera: "Fecha y hora en cabecera",
    cuit_cliente: "CUIT/DNI del cliente",
    telefono_cliente: "Teléfono del cliente",
    direccion_cliente: "Dirección del cliente",
    subtotal: "Fila Subtotal",
    envio: "Fila Envío",
    descuento: "Fila Descuento",
    total: "Fila Total (final)",
    metodo_pago: "Caja método de pago",
    observaciones: "Caja Observaciones",
    footer: "Pie con agradecimiento",
  };
  return map[k] || k;
}

function labelForFontKey(k) {
  const map = {
    base_px: "Base",
    titulo_px: "Título encabezado",
    cliente_px: "Datos del cliente",
    items_px: "Filas de ítems",
    totales_px: "Filas de totales",
    total_final_px: "Total final (grande)",
  };
  return map[k] || k;
}

/* -----------------------------------------------------------------------
 * Labels amigables para el editor fiscal
 * ---------------------------------------------------------------------*/
function labelForFiscalTextKey(k) {
  const map = {
    titulo_ticket: "Título",
    label_cliente: "Etiqueta Cliente",
    label_cuit: "Etiqueta CUIT/DNI",
    label_cond: "Etiqueta Cond. IVA",
    label_pv: "Etiqueta Pto. Vta.",
    label_nro: "Etiqueta Nº",
    label_fecha: "Etiqueta Fecha",
    label_subtotal: "Etiqueta Subtotal",
    label_neto: "Etiqueta Neto Gravado",
    label_iva: "Etiqueta IVA 21%",
    label_iva_105: "Etiqueta IVA 10.5%",
    label_no_gravado: "Etiqueta No Gravado",
    label_total: "Etiqueta TOTAL",
    label_pago: "Etiqueta Forma de Pago",
    label_vuelto: "Etiqueta Vuelto",
    label_cae: "Etiqueta CAE",
    label_cae_venc: "Etiqueta Vto. CAE",
    footer: "Texto de pie",
    col_cantidad: "Columna Cant.",
    col_detalle: "Columna Detalle",
    col_pu: "Columna P.U.",
    col_importe: "Columna Importe",
  };
  return map[k] || k;
}

function labelForFiscalSectionKey(k) {
  const map = {
    logo: "Logo empresa",
    emisor_razon_social: "Razón social del emisor",
    emisor_cuit: "CUIT del emisor",
    emisor_condicion: "Condición IVA del emisor",
    emisor_ib: "Ingresos Brutos",
    emisor_inicio_act: "Inicio de actividades",
    emisor_domicilio: "Domicilio comercial",
    letra_comprobante: "Letra grande (A/B/C)",
    tipo_comprobante: "Nombre del comprobante",
    punto_venta_numero: "Pto. Vta. + Nº",
    fecha_cabecera: "Fecha y hora",
    cliente_nombre: "Nombre del cliente",
    cuit_cliente: "CUIT/DNI del cliente",
    condicion_cliente: "Condición IVA del cliente",
    columna_pu: "Columna Precio Unit.",
    columna_importe: "Columna Importe",
    subtotal: "Subtotal (si difiere del total)",
    iva_discriminado: "IVA discriminado (Factura A)",
    total: "Total final",
    metodo_pago: "Forma de pago",
    vuelto: "Vuelto (si aplica)",
    cae: "Número de CAE",
    cae_vencimiento: "Vencimiento del CAE",
    qr_afip: "Código QR AFIP",
    footer: "Pie con agradecimiento",
  };
  return map[k] || k;
}

function labelForFiscalFontKey(k) {
  const map = {
    base_px: "Base",
    titulo_px: "Título",
    letra_px: "Letra grande A/B/C",
    emisor_px: "Datos del emisor",
    cliente_px: "Datos del cliente",
    items_px: "Filas de ítems",
    totales_px: "Filas de totales",
    total_final_px: "Total final (grande)",
    cae_px: "CAE / vencimiento",
  };
  return map[k] || k;
}


/* -----------------------------------------------------------------------
 * Configuración de Auto-Impresión de Delivery (POS)
 * Persiste en `db.settings` bajo la key `pos_delivery_autoprint`.
 * ---------------------------------------------------------------------*/
function AutoprintConfigPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pos/config/auto-print");
        setCfg({
          enabled: data?.enabled !== false,
          hora: Number.isFinite(+data?.hora) ? +data.hora : 17,
        });
      } catch (_) {
        setCfg({ enabled: true, hora: 17 });
      }
    })();
  }, []);

  if (!cfg) return null;

  const update = (patch) => {
    setCfg((prev) => ({ ...prev, ...patch }));
    setDirty(true);
  };

  const guardar = async () => {
    if (!Number.isFinite(+cfg.hora) || +cfg.hora < 0 || +cfg.hora > 23) {
      toast.error("Hora inválida (usar 0-23)");
      return;
    }
    setSaving(true);
    try {
      await api.put("/pos/config/auto-print", { enabled: !!cfg.enabled, hora: +cfg.hora });
      toast.success("Configuración guardada");
      setDirty(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="border border-[#E5E5E5] bg-white rounded-sm p-4"
      data-testid="autoprint-config-panel"
    >
      <div className="font-heading font-semibold text-[14px] mb-1">
        Auto-impresión de tickets de Delivery
      </div>
      <div className="text-[12px] text-[#666] mb-3">
        Los pedidos con <b>fecha de envío = hoy</b> se imprimen apenas se detectan.
        Los pedidos para <b>días futuros</b> se difieren hasta la hora configurada abajo.
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <label className="flex items-center gap-2 text-[13px] select-none">
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={(e) => update({ enabled: e.target.checked })}
            className="h-4 w-4"
            data-testid="autoprint-enabled"
          />
          Habilitar auto-impresión
        </label>
        <div className="flex flex-col gap-1">
          <Label className="text-[11px] text-[#666]">Hora para pedidos futuros (0-23)</Label>
          <NumberInput
            min={0}
            max={23}
            value={cfg.hora}
            onChange={(e) => update({ hora: e.target.value === "" ? "" : parseInt(e.target.value, 10) })}
            disabled={!cfg.enabled}
            className="w-24 h-9 rounded-sm"
            data-testid="autoprint-hora"
          />
        </div>
        <Button
          onClick={guardar}
          disabled={saving || !dirty}
          className="rounded-sm h-9 bg-[#6B1F24] hover:bg-[#571519]"
          data-testid="autoprint-save"
        >
          <FloppyDisk size={14} className="mr-1" /> {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
}

/* -----------------------------------------------------------------------
 * Configuración de código de barras de balanza (POS)
 * Persiste en `db.settings` bajo la key `pos_barcode_balanza`.
 * ---------------------------------------------------------------------*/
function BarcodeBalanzaPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [test, setTest] = useState("2000123001500");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/pos/config/barcode-balanza");
        setCfg(data);
      } catch (_) {
        setCfg({ enabled: true, variants: [{
          id: "default", nombre: "Por peso (gramos)",
          prefix: "2", suffix: "", length: 13,
          codigo_start: 1, codigo_len: 6, peso_start: 7, peso_len: 6,
          peso_divisor: 1000, strip_leading_zeros: true,
        }] });
      }
    })();
  }, []);

  if (!cfg) return null;

  const variants = cfg.variants || [];

  const setEnabled = (v) => { setCfg((p) => ({ ...p, enabled: v })); setDirty(true); };
  const updateVariant = (idx, patch) => {
    setCfg((p) => {
      const arr = (p.variants || []).slice();
      arr[idx] = { ...arr[idx], ...patch };
      return { ...p, variants: arr };
    });
    setDirty(true);
  };
  const removeVariant = (idx) => {
    setCfg((p) => ({ ...p, variants: (p.variants || []).filter((_, i) => i !== idx) }));
    setDirty(true);
  };
  const addVariant = () => {
    setCfg((p) => ({
      ...p,
      variants: [...(p.variants || []), {
        id: `v-${Date.now()}`, nombre: `Variante ${(p.variants || []).length + 1}`,
        prefix: "2", suffix: "", length: 13,
        codigo_start: 1, codigo_len: 6, peso_start: 7, peso_len: 6,
        peso_divisor: 1000, strip_leading_zeros: true,
      }],
    }));
    setDirty(true);
  };

  const guardar = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/pos/config/barcode-balanza", {
        enabled: !!cfg.enabled,
        variants: (cfg.variants || []).map((v) => ({
          id: v.id || null,
          nombre: v.nombre || "",
          prefix: String(v.prefix || ""),
          suffix: String(v.suffix || ""),
          length: Number(v.length),
          codigo_start: Number(v.codigo_start),
          codigo_len: Number(v.codigo_len),
          peso_start: Number(v.peso_start),
          peso_len: Number(v.peso_len),
          peso_divisor: Number(v.peso_divisor),
          strip_leading_zeros: !!v.strip_leading_zeros,
        })),
      });
      setCfg(data);
      setDirty(false);
      toast.success("Configuración guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  // Simulador: prueba el barcode contra TODAS las variantes en orden
  // (más específicas primero — mayor longitud combinada de prefijo+sufijo)
  const simular = () => {
    const c = String(test || "").trim();
    const ordenadas = variants.slice().sort((a, b) =>
      (String(b.prefix || "").length + String(b.suffix || "").length) -
      (String(a.prefix || "").length + String(a.suffix || "").length)
    );
    for (const v of ordenadas) {
      if (!v.prefix || c.length !== Number(v.length)) continue;
      if (!c.startsWith(String(v.prefix))) continue;
      const sfx = String(v.suffix || "");
      if (sfx && !c.endsWith(sfx)) continue;
      if (!/^\d+$/.test(c)) continue;
      const codigoStr = c.substr(Number(v.codigo_start), Number(v.codigo_len));
      const pesoStr = c.substr(Number(v.peso_start), Number(v.peso_len));
      const kg = Number(pesoStr) / Number(v.peso_divisor || 1000);
      const codigoLimpio = v.strip_leading_zeros ? codigoStr.replace(/^0+/, "") : codigoStr;
      return { variant: v, codigo: codigoStr, codigoLimpio, kg };
    }
    return null;
  };
  const parsed = simular();

  return (
    <div className="border border-[#E5E5E5] bg-white rounded-sm p-4" data-testid="barcode-balanza-panel">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-heading font-semibold text-[14px] mb-1">Lector de balanza · Código de barras</div>
          <div className="text-[12px] text-[#666]">
            El POS evalúa cada variante en orden y usa la primera que matchea el barcode.
            El precio del producto se toma de la lista activa.
          </div>
        </div>
        <label className="flex items-center gap-2 text-[13px] select-none">
          <input
            type="checkbox"
            checked={!!cfg.enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="h-4 w-4"
            data-testid="bb-enabled"
          />
          Habilitar
        </label>
      </div>

      <div className="space-y-3">
        {variants.map((v, idx) => (
          <div key={v.id || idx} className="border border-neutral-200 rounded-sm p-3 bg-neutral-50/40" data-testid={`bb-variant-${idx}`}>
            <div className="flex items-center gap-2 mb-2">
              <Input
                value={v.nombre || ""}
                onChange={(e) => updateVariant(idx, { nombre: e.target.value })}
                className="h-8 rounded-sm font-semibold text-[13px] max-w-[320px]"
                placeholder="Nombre descriptivo"
                data-testid={`bb-name-${idx}`}
              />
              <span className="ml-auto text-[11px] font-mono text-neutral-500">#{idx + 1}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => removeVariant(idx)}
                className="h-8 text-red-600 hover:bg-red-50 rounded-sm"
                disabled={variants.length <= 1}
                data-testid={`bb-remove-${idx}`}
              >
                Eliminar
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <Label className="text-[11px] text-[#666]">Prefijo</Label>
                <Input value={v.prefix} onChange={(e) => updateVariant(idx, { prefix: e.target.value })} className="h-9 rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">Sufijo</Label>
                <Input value={v.suffix || ""} onChange={(e) => updateVariant(idx, { suffix: e.target.value })} className="h-9 rounded-sm font-mono" placeholder="(vacío)" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">Largo total</Label>
                <NumberInput value={v.length} onChange={(e) => updateVariant(idx, { length: parseInt(e.target.value, 10) })} className="h-9 rounded-sm" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">Divisor a kg</Label>
                <NumberInput step="any" value={v.peso_divisor} onChange={(e) => updateVariant(idx, { peso_divisor: parseFloat(e.target.value) })} className="h-9 rounded-sm" title="1 = ya en kg; 1000 = gramos→kg; 1000000 = mg→kg" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">Código: desde</Label>
                <NumberInput value={v.codigo_start} onChange={(e) => updateVariant(idx, { codigo_start: parseInt(e.target.value, 10) })} className="h-9 rounded-sm" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">Código: largo</Label>
                <NumberInput value={v.codigo_len} onChange={(e) => updateVariant(idx, { codigo_len: parseInt(e.target.value, 10) })} className="h-9 rounded-sm" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">Peso: desde</Label>
                <NumberInput value={v.peso_start} onChange={(e) => updateVariant(idx, { peso_start: parseInt(e.target.value, 10) })} className="h-9 rounded-sm" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">Peso: largo</Label>
                <NumberInput value={v.peso_len} onChange={(e) => updateVariant(idx, { peso_len: parseInt(e.target.value, 10) })} className="h-9 rounded-sm" />
              </div>
              <label className="flex items-center gap-2 text-[12px] col-span-2 md:col-span-4">
                <input
                  type="checkbox"
                  checked={!!v.strip_leading_zeros}
                  onChange={(e) => updateVariant(idx, { strip_leading_zeros: e.target.checked })}
                  className="h-4 w-4"
                />
                Ignorar ceros a la izquierda al buscar el producto
              </label>
            </div>
          </div>
        ))}

        <Button variant="outline" onClick={addVariant} className="w-full h-9 rounded-sm" data-testid="bb-add">
          <Plus size={14} className="mr-1" /> Agregar variante
        </Button>
      </div>

      {/* Simulador */}
      <div className="mt-3 p-3 bg-neutral-50 border border-neutral-200 rounded-sm">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-mono mb-2">Simulador</div>
        <div className="flex items-center gap-2 flex-wrap">
          <Input value={test} onChange={(e) => setTest(e.target.value)} className="h-9 rounded-sm font-mono max-w-[240px]" placeholder="Ej: 2000123001500" data-testid="bb-test" />
          <div className="text-[12px]">
            {parsed ? (
              <span className="font-mono">
                → <b className="text-[#A02836]">{parsed.variant.nombre}</b> · código <b>{parsed.codigoLimpio || parsed.codigo}</b> · peso <b>{parsed.kg.toLocaleString("es-AR", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} kg</b>
              </span>
            ) : (
              <span className="text-neutral-400">Ninguna variante matchea con este código.</span>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex justify-end">
        <Button onClick={guardar} disabled={saving || !dirty} className="rounded-sm h-9 bg-[#A02836] hover:bg-[#821E2A]" data-testid="bb-save">
          <FloppyDisk size={14} className="mr-1" /> {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
}



/* -----------------------------------------------------------------------
 * Editor específico de la Etiqueta Zebra 55x45mm de Trazabilidad (loteo/QR)
 * ---------------------------------------------------------------------*/
// 203 dpi → 8 dots por mm. La etiqueta física es 55x45 mm = 440x360 dots.
const DOTS_PER_MM = 8;
const LABEL_DOTS_W = 440;   // 55mm
const LABEL_DOTS_H = 360;   // 45mm
// Preview en px: 440px = 55mm y 360px = 45mm (1 dot ≈ 1 px, más cómodo para arrastrar).
const DOT_TO_PX = 0.8;
const PREVIEW_PX_W = Math.round(LABEL_DOTS_W * DOT_TO_PX); // 352
const PREVIEW_PX_H = Math.round(LABEL_DOTS_H * DOT_TO_PX); // 288

// Listado de todas las "piezas" que se pueden posicionar en la etiqueta.
// Cada una tiene un default en el backend y aparece como fila configurable.
// `toggle` es la key en `secciones` que la muestra/oculta.
const ZEBRA_PIEZAS = [
  { key: "logo",          label: "Logo",                  toggle: "logo",           sample: "LOGO" },
  { key: "nombre",        label: "Nombre del producto",   toggle: "nombre",         sample: "VACÍO Y PECHITO" },
  { key: "variante",      label: "Variante / descripción (en combos: nombre del producto, se imprime siempre)",toggle: "variante",       sample: "Corte especial" },
  { key: "separador_1",   label: "Separador (línea 1)",   toggle: "separador_1",    sample: "──────────────" },
  { key: "peso",          label: "Peso neto",             toggle: "peso",           sample: "NETO: 5,432 kg" },
  { key: "fechas",        label: "Elaboración + Vencim.", toggle: "fechas",         sample: "Elab: 15/02/26   Venc: 22/02/26" },
  { key: "lote",          label: "Nº de lote",            toggle: "lote",           sample: "LOTE: 032-26" },
  { key: "separador_2",   label: "Separador (línea 2)",   toggle: "separador_2",    sample: "──────────────" },
  { key: "qr",            label: "Código QR",             toggle: "qr",             sample: "▣▣ QR ▣▣" },
  { key: "frigorifico",   label: "Razón social",          toggle: "frigorifico",    sample: "LA BIFERIA S.R.L." },
  { key: "cuit",          label: "CUIT",                  toggle: "cuit",           sample: "CUIT 30-11111111-1" },
  { key: "linea_lateral", label: "Datos lateral (repetición)", toggle: "linea_lateral", sample: "Elab: 15/02/26\nVenc: 22/02/26" },
];

function ZebraTraceabilityEditor({ config, defaults, patchConfig }) {
  const textos = config.textos || {};
  const secciones = config.secciones || {};
  const fuentes = config.fuentes || {};
  const posiciones = config.posiciones || {};
  const customTexts = config.custom_texts || [];
  const defPos = defaults.posiciones || {};

  const setTexto = (k, v) => patchConfig((c) => { c.textos = { ...(c.textos || {}), [k]: v }; });
  const setSeccion = (k, v) => patchConfig((c) => { c.secciones = { ...(c.secciones || {}), [k]: v }; });
  const setFuente = (k, v) => patchConfig((c) => { c.fuentes = { ...(c.fuentes || {}), [k]: v }; });
  const setPos = (k, patch) => {
    patchConfig((c) => {
      c.posiciones = c.posiciones || {};
      const cur = c.posiciones[k] || defPos[k] || { x: 0, y: 0, w: LABEL_DOTS_W, h: 20 };
      c.posiciones[k] = { ...cur, ...patch };
    });
  };
  const resetPos = (k) => {
    if (!defPos[k]) return;
    patchConfig((c) => {
      c.posiciones = c.posiciones || {};
      c.posiciones[k] = { ...defPos[k] };
    });
  };
  // Ocultar una pieza standard (equivalente a destildar en Secciones)
  const ocultarPieza = (pz) => {
    const toggleKey = pz.toggle;
    if (!toggleKey) return;
    if (pz.key === "fechas") {
      patchConfig((c) => {
        c.secciones = { ...(c.secciones || {}), fecha_elab: false, fecha_venc: false };
      });
      return;
    }
    patchConfig((c) => { c.secciones = { ...(c.secciones || {}), [toggleKey]: false }; });
  };
  const mostrarPieza = (pz) => {
    if (pz.key === "fechas") {
      patchConfig((c) => {
        c.secciones = { ...(c.secciones || {}), fecha_elab: true, fecha_venc: true };
      });
      return;
    }
    patchConfig((c) => { c.secciones = { ...(c.secciones || {}), [pz.toggle]: true }; });
  };

  // Custom text boxes
  const addCustomText = () => {
    patchConfig((c) => {
      c.custom_texts = c.custom_texts || [];
      const id = `ct-${Date.now()}`;
      c.custom_texts.push({
        id,
        text: "Nuevo texto",
        x: 80, y: 80, w: 240, h: 30, font_h: 24,
        align: "L", bold: false, italic: false, underline: false,
      });
    });
  };
  const updateCustomText = (id, patch) => {
    patchConfig((c) => {
      c.custom_texts = (c.custom_texts || []).map((ct) => ct.id === id ? { ...ct, ...patch } : ct);
    });
  };
  const removeCustomText = (id) => {
    patchConfig((c) => {
      c.custom_texts = (c.custom_texts || []).filter((ct) => ct.id !== id);
    });
  };

  // Estado de arrastre en el canvas — sólo mientras se está moviendo una pieza
  const [dragKey, setDragKey] = useState(null);
  const [dragOffset, setDragOffset] = useState({ dx: 0, dy: 0 });

  const isVisible = (pz) => {
    // Caso especial: la pieza "fechas" agrupa ambas fechas; se muestra si al menos
    // una de las dos está habilitada.
    if (pz.key === "fechas") {
      return secciones.fecha_elab !== false || secciones.fecha_venc !== false;
    }
    // Caso especial: el logo requiere además que exista una imagen cargada.
    if (pz.key === "logo") {
      return secciones.logo === true && !!(config.logo && config.logo.data_url);
    }
    return pz.toggle == null ? true : secciones[pz.toggle] !== false;
  };

  const getPos = (k) => {
    const p = posiciones[k] || defPos[k] || { x: 0, y: 0, w: LABEL_DOTS_W, h: 20 };
    return {
      x: Number.isFinite(+p.x) ? +p.x : 0,
      y: Number.isFinite(+p.y) ? +p.y : 0,
      w: Number.isFinite(+p.w) ? +p.w : LABEL_DOTS_W,
      h: Number.isFinite(+p.h) ? +p.h : 20,
    };
  };

  // Conversores mm ↔ dots
  const dotsToMm = (d) => (d / DOTS_PER_MM);
  const mmToDots = (mm) => Math.round(Number(mm) * DOTS_PER_MM);

  const onCanvasMouseMove = (e) => {
    if (!dragKey) return;
    const canvas = e.currentTarget.getBoundingClientRect();
    const xPx = e.clientX - canvas.left - dragOffset.dx;
    const yPx = e.clientY - canvas.top - dragOffset.dy;
    const xDots = Math.max(0, Math.min(LABEL_DOTS_W, Math.round(xPx / DOT_TO_PX)));
    const yDots = Math.max(0, Math.min(LABEL_DOTS_H, Math.round(yPx / DOT_TO_PX)));
    if (dragKey.startsWith("__ct__")) {
      const id = dragKey.slice("__ct__".length);
      updateCustomText(id, { x: xDots, y: yDots });
    } else {
      setPos(dragKey, { x: xDots, y: yDots });
    }
  };

  const onPieceMouseDown = (e, k) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setDragKey(k);
    setDragOffset({ dx: e.clientX - rect.left, dy: e.clientY - rect.top });
  };

  const endDrag = () => setDragKey(null);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-4">
      <div className="border border-[#E5E5E5] bg-white rounded-sm p-4 space-y-5">
        <div>
          <div className="font-heading font-semibold text-[14px] mb-1">Etiqueta Zebra · Trazabilidad</div>
          <div className="text-[12px] text-[#666]">
            Etiqueta 55 × 45 mm (440 × 360 dots @ 203 dpi). Configurá qué campos aparecen, sus textos, tamaños de fuente
            y <b>la posición exacta</b> de cada pieza.
          </div>
        </div>

        <Tabs defaultValue="posiciones">
          <TabsList className="w-full justify-start rounded-none border-b border-[#E5E5E5] bg-neutral-50 h-11 px-2">
            <TabsTrigger value="posiciones" data-testid="tab-zebra-posiciones">Posiciones</TabsTrigger>
            <TabsTrigger value="secciones" data-testid="tab-zebra-secciones">Secciones</TabsTrigger>
            <TabsTrigger value="logo" data-testid="tab-zebra-logo">Logo</TabsTrigger>
            <TabsTrigger value="textos" data-testid="tab-zebra-textos">Textos</TabsTrigger>
            <TabsTrigger value="fuentes" data-testid="tab-zebra-fuentes">Fuentes</TabsTrigger>
          </TabsList>

          {/* --- Posiciones --- */}
          <TabsContent value="posiciones" className="pt-4">
            <div className="text-[12px] text-[#666] mb-2">
              Valores en <b>milímetros</b>. También podés arrastrar cada pieza directamente en la vista previa.
              El campo <b>Ancho</b> controla el wrapping del texto en la impresión.
            </div>
            <div className="border border-neutral-200 rounded-sm overflow-hidden">
              <table className="w-full text-[12px] font-mono">
                <thead className="bg-neutral-50 text-[11px] uppercase tracking-widest text-[#666]">
                  <tr>
                    <th className="text-left px-2 py-2">Pieza</th>
                    <th className="text-right px-2 py-2 w-16">X (mm)</th>
                    <th className="text-right px-2 py-2 w-16">Y (mm)</th>
                    <th className="text-right px-2 py-2 w-16">Ancho (mm)</th>
                    <th className="text-right px-2 py-2 w-16">Alto (mm)</th>
                    <th className="px-2 py-2 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {ZEBRA_PIEZAS.map((pz) => {
                    const p = getPos(pz.key);
                    const visible = isVisible(pz);
                    return (
                      <tr key={pz.key} className={`border-t border-neutral-200 ${visible ? "" : "opacity-40"}`}>
                        <td className="px-2 py-1.5">{pz.label}</td>
                        {[
                          ["x", p.x],
                          ["y", p.y],
                          ["w", p.w],
                          ["h", p.h],
                        ].map(([field, val]) => (
                          <td key={field} className="px-1 py-1">
                            <NumberInput
                              step="0.5"
                              value={dotsToMm(val).toFixed(1)}
                              onChange={(e) => setPos(pz.key, { [field]: mmToDots(e.target.value || 0) })}
                              className="h-8 rounded-sm text-right font-mono text-[12px] px-1"
                              data-testid={`zebra-pos-${pz.key}-${field}`}
                            />
                          </td>
                        ))}
                        <td className="px-1 py-1 text-center">
                          <div className="flex items-center gap-1 justify-center">
                            <button
                              type="button"
                              onClick={() => resetPos(pz.key)}
                              className="text-[11px] text-[#6B1F24] hover:underline"
                              title="Restaurar posición default"
                              data-testid={`zebra-pos-${pz.key}-reset`}
                            >
                              reset
                            </button>
                            {visible ? (
                              <button
                                type="button"
                                onClick={() => ocultarPieza(pz)}
                                className="p-1 rounded hover:bg-red-50 text-red-600"
                                title="Eliminar (ocultar de la etiqueta)"
                                data-testid={`zebra-pos-${pz.key}-del`}
                              >
                                <X size={12} />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => mostrarPieza(pz)}
                                className="p-1 rounded hover:bg-emerald-50 text-emerald-700"
                                title="Volver a mostrar"
                                data-testid={`zebra-pos-${pz.key}-show`}
                              >
                                <Plus size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {/* Cuadros de texto libres */}
                  {customTexts.map((ct) => (
                    <tr key={ct.id} className="border-t border-neutral-200 bg-amber-50/30">
                      <td className="px-2 py-1.5">
                        <Input
                          value={ct.text || ""}
                          onChange={(e) => updateCustomText(ct.id, { text: e.target.value })}
                          className="h-8 rounded-sm font-mono text-[12px] px-2"
                          placeholder="Texto libre…"
                          data-testid={`zebra-ct-text-${ct.id}`}
                          style={{
                            fontWeight: ct.bold ? "bold" : "normal",
                            fontStyle: ct.italic ? "italic" : "normal",
                            textDecoration: ct.underline ? "underline" : "none",
                          }}
                        />
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <Label className="text-[10px] text-[#666]">Fuente</Label>
                          <NumberInput
                            value={ct.font_h || 24}
                            onChange={(e) => updateCustomText(ct.id, { font_h: parseInt(e.target.value, 10) || 24 })}
                            className="h-7 w-14 rounded-sm text-right font-mono text-[11px] px-1"
                            data-testid={`zebra-ct-font-${ct.id}`}
                          />
                          {/* Alineación */}
                          <div className="inline-flex border border-neutral-300 rounded-sm ml-1">
                            {[
                              ["L", "Izquierda"],
                              ["C", "Centro"],
                              ["R", "Derecha"],
                            ].map(([code, title]) => (
                              <button
                                key={code}
                                type="button"
                                onClick={() => updateCustomText(ct.id, { align: code })}
                                title={title}
                                className={`h-7 w-7 text-[11px] font-mono ${
                                  (ct.align || "L") === code ? "bg-[#921A0E] text-white" : "bg-white hover:bg-neutral-100"
                                }`}
                                data-testid={`zebra-ct-align-${ct.id}-${code}`}
                              >
                                {code}
                              </button>
                            ))}
                          </div>
                          {/* Bold / Italic / Underline */}
                          <div className="inline-flex border border-neutral-300 rounded-sm ml-1">
                            <button
                              type="button"
                              onClick={() => updateCustomText(ct.id, { bold: !ct.bold })}
                              title="Negrita"
                              className={`h-7 w-7 text-[11px] font-bold ${ct.bold ? "bg-[#921A0E] text-white" : "bg-white hover:bg-neutral-100"}`}
                              data-testid={`zebra-ct-bold-${ct.id}`}
                            >B</button>
                            <button
                              type="button"
                              onClick={() => updateCustomText(ct.id, { italic: !ct.italic })}
                              title="Cursiva (solo pantalla · ZPL no soporta italic)"
                              className={`h-7 w-7 text-[11px] italic ${ct.italic ? "bg-[#921A0E] text-white" : "bg-white hover:bg-neutral-100"}`}
                              data-testid={`zebra-ct-italic-${ct.id}`}
                            >I</button>
                            <button
                              type="button"
                              onClick={() => updateCustomText(ct.id, { underline: !ct.underline })}
                              title="Subrayado"
                              className={`h-7 w-7 text-[11px] underline ${ct.underline ? "bg-[#921A0E] text-white" : "bg-white hover:bg-neutral-100"}`}
                              data-testid={`zebra-ct-underline-${ct.id}`}
                            >U</button>
                          </div>
                        </div>
                      </td>
                      {[
                        ["x", ct.x],
                        ["y", ct.y],
                        ["w", ct.w],
                        ["h", ct.h],
                      ].map(([field, val]) => (
                        <td key={field} className="px-1 py-1">
                          <NumberInput
                            step="0.5"
                            value={dotsToMm(val || 0).toFixed(1)}
                            onChange={(e) => updateCustomText(ct.id, { [field]: mmToDots(e.target.value || 0) })}
                            className="h-8 rounded-sm text-right font-mono text-[12px] px-1"
                            data-testid={`zebra-ct-${ct.id}-${field}`}
                          />
                        </td>
                      ))}
                      <td className="px-1 py-1 text-center">
                        <button
                          type="button"
                          onClick={() => removeCustomText(ct.id)}
                          className="p-1 rounded hover:bg-red-50 text-red-600"
                          title="Eliminar cuadro"
                          data-testid={`zebra-ct-remove-${ct.id}`}
                        >
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-3">
              <Button
                variant="outline"
                onClick={addCustomText}
                className="rounded-sm h-9 gap-1"
                data-testid="zebra-add-custom-text"
              >
                <Plus size={14} /> Agregar cuadro de texto
              </Button>
            </div>
          </TabsContent>

          {/* --- Secciones --- */}
          <TabsContent value="secciones" className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                ["nombre", "Nombre del producto"],
                ["variante", "Variante / descripción"],
                ["peso", "Peso neto"],
                ["fecha_elab", "Fecha de elaboración"],
                ["fecha_venc", "Fecha de vencimiento"],
                ["lote", "Nº de lote"],
                ["qr", "Código QR (trazabilidad)"],
                ["frigorifico", "Razón social del frigorífico"],
                ["cuit", "CUIT del frigorífico"],
                ["separador_1", "Separador (línea 1)"],
                ["separador_2", "Separador (línea 2)"],
                ["linea_lateral", "Datos lateral (repetición debajo del CUIT)"],
              ].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-[13px] border border-neutral-200 rounded-sm px-2 py-1.5 hover:bg-neutral-50 select-none">
                  <input
                    type="checkbox"
                    checked={secciones[k] !== false}
                    onChange={(e) => setSeccion(k, e.target.checked)}
                    className="h-4 w-4"
                    data-testid={`zebra-sec-${k}`}
                  />
                  {label}
                </label>
              ))}
            </div>
          </TabsContent>

          {/* --- Logo --- */}
          <TabsContent value="logo" className="pt-4 space-y-3">
            <div className="text-[12px] text-[#666]">
              Subí una imagen (PNG/JPG) para que aparezca en la etiqueta. Se convierte a blanco y negro
              (thresholding) y se envía por ZPL. La posición y el tamaño se ajustan desde la pestaña <b>Posiciones</b>
              (fila "Logo").
            </div>
            {config.logo && config.logo.data_url ? (
              <div className="flex items-start gap-4">
                <div className="border border-neutral-200 rounded-sm p-2 bg-neutral-50">
                  <img
                    src={config.logo.data_url}
                    alt="logo"
                    className="max-w-[180px] max-h-[120px] object-contain"
                  />
                  <div className="text-[10px] font-mono text-neutral-500 mt-1">
                    {(config.logo.width_dots || 0)}×{(config.logo.height_dots || 0)} dots
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer border border-neutral-300 hover:bg-neutral-50 rounded-sm px-3 h-9">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                      className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        try {
                          const dataUrl = await new Promise((res, rej) => {
                            const rd = new FileReader();
                            rd.onload = () => res(rd.result);
                            rd.onerror = rej;
                            rd.readAsDataURL(file);
                          });
                          // Convertir a ZPL dentro del cuadro definido en posiciones.logo
                          const posLogo = (config.posiciones && config.posiciones.logo) || defPos.logo || { x: 8, y: 8, w: 80, h: 60 };
                          const out = await imagenAZplGfa(dataUrl, posLogo.w || 80, posLogo.h || 60);
                          patchConfig((c) => {
                            c.logo = out;
                            c.secciones = { ...(c.secciones || {}), logo: true };
                          });
                        } catch (err) {
                          console.error("Error cargando logo:", err);
                          window.alert("No se pudo procesar la imagen: " + (err.message || err));
                        }
                        e.target.value = "";
                      }}
                      data-testid="zebra-logo-upload"
                    />
                    Cambiar imagen
                  </label>
                  <div>
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-sm h-9 gap-1 text-red-700 border-red-300 hover:bg-red-50"
                      onClick={() => patchConfig((c) => { c.logo = {}; c.secciones = { ...(c.secciones || {}), logo: false }; })}
                      data-testid="zebra-logo-remove"
                    >
                      <X size={14} /> Quitar logo
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 text-[13px] cursor-pointer border border-neutral-300 hover:bg-neutral-50 rounded-sm px-3 h-9">
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      const dataUrl = await new Promise((res, rej) => {
                        const rd = new FileReader();
                        rd.onload = () => res(rd.result);
                        rd.onerror = rej;
                        rd.readAsDataURL(file);
                      });
                      const posLogo = (config.posiciones && config.posiciones.logo) || defPos.logo || { x: 8, y: 8, w: 80, h: 60 };
                      const out = await imagenAZplGfa(dataUrl, posLogo.w || 80, posLogo.h || 60);
                      patchConfig((c) => {
                        c.logo = out;
                        c.secciones = { ...(c.secciones || {}), logo: true };
                      });
                    } catch (err) {
                      console.error("Error cargando logo:", err);
                      window.alert("No se pudo procesar la imagen: " + (err.message || err));
                    }
                    e.target.value = "";
                  }}
                  data-testid="zebra-logo-upload"
                />
                <Plus size={14} /> Subir logo
              </label>
            )}
          </TabsContent>

          {/* --- Textos --- */}
          <TabsContent value="textos" className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-[#666]">Razón social</Label>
                <Input value={textos.frigorifico || ""} onChange={(e) => setTexto("frigorifico", e.target.value)} className="h-9 rounded-sm" data-testid="zebra-txt-frigorifico" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">CUIT del frigorífico</Label>
                <Input value={textos.cuit || ""} onChange={(e) => setTexto("cuit", e.target.value)} className="h-9 rounded-sm" placeholder="30-11111111-1" data-testid="zebra-txt-cuit" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">Prefijo "Neto"</Label>
                <Input value={textos.label_neto || "NETO:"} onChange={(e) => setTexto("label_neto", e.target.value)} className="h-9 rounded-sm" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">Prefijo "Elab"</Label>
                <Input value={textos.label_elab || "Elab:"} onChange={(e) => setTexto("label_elab", e.target.value)} className="h-9 rounded-sm" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">Prefijo "Venc"</Label>
                <Input value={textos.label_venc || "Venc:"} onChange={(e) => setTexto("label_venc", e.target.value)} className="h-9 rounded-sm" />
              </div>
              <div>
                <Label className="text-[11px] text-[#666]">Prefijo "Lote"</Label>
                <Input value={textos.label_lote || "LOTE:"} onChange={(e) => setTexto("label_lote", e.target.value)} className="h-9 rounded-sm" />
              </div>
            </div>
          </TabsContent>

          {/* --- Fuentes --- */}
          <TabsContent value="fuentes" className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              {[
                ["nombre_h", "Nombre"],
                ["variante_h", "Variante"],
                ["peso_h", "Peso"],
                ["lote_h", "Lote"],
                ["fechas_h", "Fechas"],
              ].map(([k, label]) => (
                <div key={k}>
                  <Label className="text-[11px] text-[#666]">{label}</Label>
                  <NumberInput
                    value={fuentes[k] || ""}
                    onChange={(e) => setFuente(k, parseInt(e.target.value, 10) || 0)}
                    className="h-9 rounded-sm"
                    data-testid={`zebra-fnt-${k}`}
                  />
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Preview visual con posicionamiento absoluto y drag & drop */}
      <div className="border border-[#E5E5E5] bg-white rounded-sm p-3 sticky top-4 self-start">
        <div className="text-[11px] uppercase tracking-widest text-neutral-500 font-mono mb-2">
          Vista previa · arrastrable
        </div>
        <div
          className="mx-auto border-2 border-neutral-500 bg-white rounded-sm relative select-none"
          style={{ width: PREVIEW_PX_W, height: PREVIEW_PX_H, fontFamily: "monospace", cursor: dragKey ? "grabbing" : "default" }}
          onMouseMove={onCanvasMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          data-testid="zebra-canvas"
        >
          {ZEBRA_PIEZAS.map((pz) => {
            if (!isVisible(pz)) return null;
            const p = getPos(pz.key);
            const x = p.x * DOT_TO_PX;
            const y = p.y * DOT_TO_PX;
            const w = p.w * DOT_TO_PX;
            const h = p.h * DOT_TO_PX;
            // Escala del texto para preview (px). 1 dot ≈ 0.125 mm.
            // Los tamaños ZPL están en dots ≈ mismo número que px del preview (¡conveniente!),
            // pero ajustamos con DOT_TO_PX para respetar el zoom.
            const fontMap = {
              nombre: (fuentes.nombre_h || 32) * DOT_TO_PX,
              variante: (fuentes.variante_h || 24) * DOT_TO_PX,
              peso: (fuentes.peso_h || 38) * DOT_TO_PX,
              lote: (fuentes.lote_h || 32) * DOT_TO_PX,
              fechas: (fuentes.fechas_h || 20) * DOT_TO_PX,
              frigorifico: 20 * DOT_TO_PX,
              cuit: 18 * DOT_TO_PX,
              linea_lateral: 16 * DOT_TO_PX,
            };
            const isSep = pz.key.startsWith("separador");
            const isQR = pz.key === "qr";
            const isLogo = pz.key === "logo";

            let content;
            if (isSep) {
              content = <div className="w-full h-full bg-black" />;
            } else if (isQR) {
              content = (
                <div
                  className="w-full h-full bg-neutral-900"
                  style={{
                    backgroundImage: "linear-gradient(45deg, #000 25%, transparent 25%), linear-gradient(-45deg, #000 25%, transparent 25%)",
                    backgroundSize: "6px 6px",
                  }}
                />
              );
            } else if (isLogo) {
              content = config.logo && config.logo.data_url ? (
                <img
                  src={config.logo.data_url}
                  alt="logo"
                  className="w-full h-full object-contain"
                  draggable={false}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-neutral-100 text-neutral-400 text-[10px] font-mono">
                  LOGO
                </div>
              );
            } else {
              const rendered = renderPreviewText(pz.key, textos, secciones);
              content = (
                <div
                  className="whitespace-pre leading-tight overflow-hidden font-bold"
                  style={{ fontSize: fontMap[pz.key] || 12 * DOT_TO_PX }}
                >
                  {rendered || pz.sample}
                </div>
              );
            }

            const active = dragKey === pz.key;
            return (
              <div
                key={pz.key}
                onMouseDown={(e) => onPieceMouseDown(e, pz.key)}
                title={`${pz.label} — arrastrar para mover`}
                className={`absolute box-border ${active ? "outline outline-2 outline-[#6B1F24]" : "hover:outline hover:outline-1 hover:outline-[#6B1F24]/60"}`}
                style={{
                  left: x, top: y, width: w, height: h,
                  cursor: active ? "grabbing" : "grab",
                }}
                data-testid={`zebra-piece-${pz.key}`}
              >
                {content}
              </div>
            );
          })}

          {/* Cuadros de texto libres */}
          {customTexts.map((ct) => {
            const x = (ct.x || 0) * DOT_TO_PX;
            const y = (ct.y || 0) * DOT_TO_PX;
            const w = (ct.w || 100) * DOT_TO_PX;
            const h = (ct.h || 20) * DOT_TO_PX;
            const fs = (ct.font_h || 24) * DOT_TO_PX;
            const dragId = `__ct__${ct.id}`;
            const active = dragKey === dragId;
            return (
              <div
                key={ct.id}
                onMouseDown={(e) => onPieceMouseDown(e, dragId)}
                title={`Cuadro libre — arrastrar`}
                className={`absolute box-border bg-amber-50/40 ${active ? "outline outline-2 outline-amber-500" : "outline outline-1 outline-amber-400/60 hover:outline-amber-500"}`}
                style={{ left: x, top: y, width: w, height: h, cursor: active ? "grabbing" : "grab" }}
                data-testid={`zebra-piece-ct-${ct.id}`}
              >
                <div
                  className="whitespace-pre leading-tight overflow-hidden"
                  style={{
                    fontSize: fs,
                    fontWeight: ct.bold ? "bold" : "normal",
                    fontStyle: ct.italic ? "italic" : "normal",
                    textDecoration: ct.underline ? "underline" : "none",
                    textAlign: ct.align === "C" ? "center" : ct.align === "R" ? "right" : "left",
                    width: "100%",
                  }}
                >
                  {ct.text || "Texto libre"}
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-[10px] text-neutral-500 font-mono text-center mt-2">
          55 × 45 mm · 440 × 360 dots · arrastrá cada pieza
        </div>
      </div>
    </div>
  );
}

// Render textual de cada pieza en la vista previa
function renderPreviewText(key, textos, secciones = {}) {
  switch (key) {
    case "nombre": return "VACÍO Y PECHITO";
    case "variante": return "CORTE ESPECIAL";
    case "peso": return `${textos.label_neto || "NETO:"} 5,432 kg`;
    case "fechas": {
      const parts = [];
      if (secciones.fecha_elab !== false) parts.push(`${textos.label_elab || "Elab:"} 15/02/26`);
      if (secciones.fecha_venc !== false) parts.push(`${textos.label_venc || "Venc:"} 22/02/26`);
      return parts.join("   ");
    }
    case "lote": return `${textos.label_lote || "LOTE:"} 032-26`;
    case "frigorifico": return (textos.frigorifico || "LA BIFERIA S.R.L.").toUpperCase();
    case "cuit": return textos.cuit ? `CUIT ${textos.cuit}` : "CUIT 30-11111111-1";
    case "linea_lateral": {
      const parts = [];
      if (secciones.fecha_elab !== false) parts.push(`${textos.label_elab || "Elab:"} 15/02/26`);
      if (secciones.fecha_venc !== false) parts.push(`${textos.label_venc || "Venc:"} 22/02/26`);
      parts.push(`Lote 032-26`);
      return parts.join("\n");
    }
    default: return "";
  }
}

/* -----------------------------------------------------------------------
 * Convierte un data URL de imagen en el comando ZPL ^GFA para embeberla
 * en la etiqueta Zebra. Downscala manteniendo aspect ratio dentro del
 * cuadro width_dots × height_dots. Usa threshold para 1-bit b/n.
 * ---------------------------------------------------------------------*/
async function imagenAZplGfa(dataUrl, boxWidthDots, boxHeightDots) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onerror = reject;
    img.onload = () => {
      try {
        const boxW = Math.max(8, Math.round(boxWidthDots));
        const boxH = Math.max(8, Math.round(boxHeightDots));
        // Mantener aspect ratio dentro del cuadro
        const ratio = Math.min(boxW / img.width, boxH / img.height);
        const dstW = Math.max(8, Math.floor(img.width * ratio));
        const dstH = Math.max(8, Math.floor(img.height * ratio));
        // ZPL requiere que cada fila sea múltiplo de 8 bits → redondeamos ancho a múltiplo de 8
        const rowBytes = Math.ceil(dstW / 8);
        const finalW = rowBytes * 8;
        const canvas = document.createElement("canvas");
        canvas.width = finalW;
        canvas.height = dstH;
        const ctx = canvas.getContext("2d");
        // Fondo blanco
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, finalW, dstH);
        // Centrar la imagen horizontalmente si finalW > dstW
        const offX = Math.floor((finalW - dstW) / 2);
        ctx.drawImage(img, offX, 0, dstW, dstH);
        const imgData = ctx.getImageData(0, 0, finalW, dstH);
        const data = imgData.data;
        // Convertir a 1 bit (threshold sobre luminancia)
        const hex = [];
        for (let y = 0; y < dstH; y++) {
          for (let bx = 0; bx < rowBytes; bx++) {
            let byte = 0;
            for (let bit = 0; bit < 8; bit++) {
              const x = bx * 8 + bit;
              const idx = (y * finalW + x) * 4;
              const r = data[idx], g = data[idx + 1], b = data[idx + 2], a = data[idx + 3];
              // Si es transparente lo tratamos como blanco
              const lum = a < 128 ? 255 : (0.299 * r + 0.587 * g + 0.114 * b);
              const isBlack = lum < 128;
              if (isBlack) byte |= (1 << (7 - bit));
            }
            hex.push(byte.toString(16).padStart(2, "0").toUpperCase());
          }
        }
        const zplHex = hex.join("");
        const totalBytes = rowBytes * dstH;
        resolve({
          data_url: dataUrl,
          zpl_hex: zplHex,
          width_dots: finalW,
          height_dots: dstH,
          bytes_per_row: rowBytes,
          total_bytes: totalBytes,
        });
      } catch (err) {
        reject(err);
      }
    };
    img.src = dataUrl;
  });
}


